/**
 * LoRA API client.
 *
 * Thin wrappers over the generated hey-api SDK (`./api/sdk.gen`). LoRA routes
 * are now part of api-alpha's OpenAPI spec, so calls and types come straight
 * from the generated SDK — the same pattern used elsewhere in the app
 * (`{ throwOnError: true, path, query, body }` → `res.data?.data`). Wrappers
 * exist only to keep the hook call sites stable and to surface a friendly
 * error message; they add no transport logic of their own.
 *
 * All routes live under `/v1/` and are prefixed automatically with `/api`
 * by the base URL configured on `client.gen.ts`.
 */

import {
  listLoras as sdkListLoras,
  getLora as sdkGetLora,
  createLora as sdkCreateLora,
  updateLora as sdkUpdateLora,
  deleteLora as sdkDeleteLora,
  transferLoraToTeam as sdkTransferLoraToTeam,
  transferLoraToUser as sdkTransferLoraToUser,
  listLoraCheckpoints as sdkListLoraCheckpoints,
  setDefaultCheckpoint as sdkSetDefaultCheckpoint,
  submitLoraTrainingRun as sdkSubmitLoraTrainingRun,
  listLoraTrainingRuns as sdkListLoraTrainingRuns,
  getLoraTrainingRun as sdkGetLoraTrainingRun,
  cancelLoraTrainingRun as sdkCancelLoraTrainingRun,
  startLoraDatasetPrep as sdkStartLoraDatasetPrep,
  getLoraDatasetPrep as sdkGetLoraDatasetPrep,
  updateLoraDatasetCaption as sdkUpdateLoraDatasetCaption,
  listLoraBaseModels as sdkListLoraBaseModels,
  listLoraKinds as sdkListLoraKinds,
} from "./api/sdk.gen";
import type {
  Checkpoint,
  CreateLoraBody,
  ListLorasFilters,
  Lora,
  LoraBaseModel,
  LoraDatasetCaption,
  LoraDatasetPrep,
  LoraKind,
  StartDatasetPrepResult,
  TrainingRun,
  TrainingRunWithCheckpoints,
  UpdateLoraBody,
} from "../types/loras";

/**
 * Generated SDK methods called with `throwOnError: true` reject on any non-2xx
 * response, so a resolved call always carries the success envelope. This pulls
 * the `data` payload out of that envelope and guards the (type-only) optional.
 */
function payload<T>(value: T | undefined | null, fallbackMsg: string): T {
  if (value === undefined || value === null) {
    throw new Error(fallbackMsg);
  }
  return value;
}

// ============================================================
// LoRA CRUD
// ============================================================

export async function listLoras(filters?: ListLorasFilters): Promise<Lora[]> {
  const res = await sdkListLoras({ throwOnError: true, query: filters });
  return res.data?.data ?? [];
}

export async function getLora(loraId: string): Promise<Lora> {
  const res = await sdkGetLora({ throwOnError: true, path: { loraId } });
  return payload(res.data?.data, "Failed to fetch LoRA");
}

export async function createLora(body: CreateLoraBody): Promise<Lora> {
  const res = await sdkCreateLora({ throwOnError: true, body });
  return payload(res.data?.data, "Failed to create LoRA");
}

export async function updateLora(
  loraId: string,
  body: UpdateLoraBody,
): Promise<Lora> {
  const res = await sdkUpdateLora({
    throwOnError: true,
    path: { loraId },
    body,
  });
  return payload(res.data?.data, "Failed to update LoRA");
}

export async function deleteLora(loraId: string): Promise<void> {
  await sdkDeleteLora({ throwOnError: true, path: { loraId } });
}

export async function transferLoraToTeam(
  loraId: string,
  teamId: string,
): Promise<Lora> {
  const res = await sdkTransferLoraToTeam({
    throwOnError: true,
    path: { loraId },
    body: { teamId },
  });
  return payload(res.data?.data, "Failed to transfer LoRA to team");
}

export async function transferLoraToUser(loraId: string): Promise<Lora> {
  const res = await sdkTransferLoraToUser({
    throwOnError: true,
    path: { loraId },
  });
  return payload(res.data?.data, "Failed to transfer LoRA to user");
}

// ============================================================
// Checkpoints
// ============================================================

export async function listCheckpoints(loraId: string): Promise<Checkpoint[]> {
  const res = await sdkListLoraCheckpoints({
    throwOnError: true,
    path: { loraId },
  });
  return res.data?.data ?? [];
}

export async function setDefaultCheckpoint(
  loraId: string,
  checkpointId: string,
): Promise<Lora> {
  const res = await sdkSetDefaultCheckpoint({
    throwOnError: true,
    path: { loraId },
    body: { checkpointId },
  });
  return payload(res.data?.data, "Failed to set default checkpoint");
}

// ============================================================
// Training runs
// ============================================================

export async function submitTrainingRun(
  loraId: string,
  params: Record<string, unknown>,
  /** When true, uses mock training (30s per checkpoint) instead of real AWS Batch. */
  mock?: boolean,
): Promise<TrainingRun> {
  const res = await sdkSubmitLoraTrainingRun({
    throwOnError: true,
    path: { loraId },
    body: { params, mock },
  });
  return payload(res.data?.data, "Failed to submit training run");
}

export async function listTrainingRuns(loraId: string): Promise<TrainingRun[]> {
  const res = await sdkListLoraTrainingRuns({
    throwOnError: true,
    path: { loraId },
  });
  return res.data?.data ?? [];
}

export async function getTrainingRun(
  trainingRunId: string,
): Promise<TrainingRunWithCheckpoints> {
  const res = await sdkGetLoraTrainingRun({
    throwOnError: true,
    path: { trainingRunId },
  });
  const run = payload(res.data?.data, "Failed to fetch training run");
  return { ...run, checkpoints: run.checkpoints ?? [] };
}

export async function cancelTrainingRun(
  trainingRunId: string,
): Promise<TrainingRun> {
  const res = await sdkCancelLoraTrainingRun({
    throwOnError: true,
    path: { trainingRunId },
  });
  return payload(res.data?.data, "Failed to cancel training run");
}

// ============================================================
// Dataset preparation ("Prepare" stage)
// ============================================================

export async function startDatasetPrep(
  loraId: string,
): Promise<StartDatasetPrepResult> {
  const res = await sdkStartLoraDatasetPrep({
    throwOnError: true,
    path: { loraId },
  });
  return payload(res.data?.data, "Failed to start dataset prep");
}

export async function getDatasetPrep(loraId: string): Promise<LoraDatasetPrep> {
  const res = await sdkGetLoraDatasetPrep({
    throwOnError: true,
    path: { loraId },
  });
  return payload(res.data?.data, "Failed to fetch dataset prep");
}

export async function updateDatasetCaption(
  loraId: string,
  assetId: string,
  caption: string,
): Promise<LoraDatasetCaption> {
  const res = await sdkUpdateLoraDatasetCaption({
    throwOnError: true,
    path: { loraId, assetId },
    body: { caption },
  });
  return payload(res.data?.data, "Failed to update caption");
}

// ============================================================
// Catalog
// ============================================================

export async function listLoraBaseModels(): Promise<LoraBaseModel[]> {
  const res = await sdkListLoraBaseModels({ throwOnError: true });
  return res.data?.data ?? [];
}

export async function listLoraKinds(): Promise<LoraKind[]> {
  const res = await sdkListLoraKinds({ throwOnError: true });
  return res.data?.data ?? [];
}
